import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface UserPurchases {
  aiCredits: number;
  unlockedFeatures: string[];
  loading: boolean;
}

export function useUserPurchases() {
  const { user } = useAuth();
  const [purchases, setPurchases] = useState<UserPurchases>({
    aiCredits: 0,
    unlockedFeatures: [],
    loading: true,
  });

  const fetchPurchases = useCallback(async () => {
    if (!user) {
      setPurchases({ aiCredits: 0, unlockedFeatures: [], loading: false });
      return;
    }
    try {
      const { data, error } = await supabase
        .from("user_purchases" as any)
        .select("ai_credits, unlocked_features")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      setPurchases({
        aiCredits: (data as any)?.ai_credits ?? 0,
        unlockedFeatures: (data as any)?.unlocked_features ?? [],
        loading: false,
      });
    } catch (err) {
      console.error("Failed to fetch purchases:", err);
      setPurchases((prev) => ({ ...prev, loading: false }));
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  const consumeCredit = useCallback(async (): Promise<boolean> => {
    if (!user || purchases.aiCredits <= 0) return false;
    try {
      const { error } = await supabase
        .from("user_purchases" as any)
        .update({ ai_credits: purchases.aiCredits - 1 } as any)
        .eq("user_id", user.id);
      if (error) throw error;
      setPurchases((prev) => ({ ...prev, aiCredits: prev.aiCredits - 1 }));
      return true;
    } catch {
      return false;
    }
  }, [user?.id, purchases.aiCredits]);

  const hasFeature = useCallback(
    (featureId: string) => purchases.unlockedFeatures.includes(featureId),
    [purchases.unlockedFeatures]
  );

  return { ...purchases, fetchPurchases, consumeCredit, hasFeature };
}
